// apolo/src/lib/loop/applyToolEffects.js — P4-T03 (code-tab-lift).
// @arch:pure-engine
//
// PURE application of a deriveToolEffects descriptor onto the loop's tool-effect
// accumulator, extracted from runLocalAgentLoopInner's post-execution block.
// deriveToolEffects (P4-T05 of software-to-9) already classifies a completed tool
// call; this applies that descriptor in the SAME order it was applied inline:
//   gatheredContext → ragChunk → (retrieve|tool markStage) → wroteFile → knownPath → committed
// The accumulator's arrays (ragChunks/writtenFiles/knownExistingPaths) are mutated in
// place (the loop passes its own refs); the scalar fields are mutated on the acc and
// written back by the caller. The markStage is a telemetry side effect, so it's
// returned as an instruction for the loop to apply (keeping this module pure).
//
// L-3: order + rules byte-identical to the inline block — do not reorder.

/**
 * @typedef {Object} ToolEffectAcc
 * @property {boolean} gatheredContext
 * @property {string[]} ragChunks
 * @property {boolean} wroteFile
 * @property {Array<{path:string,content:string}>} writtenFiles
 * @property {string[]} knownExistingPaths
 * @property {boolean} committed
 */

/**
 * Apply a deriveToolEffects descriptor onto `acc` (mutates it). Returns the markStage
 * instruction implied by the effect, or null — the caller applies it to turnTrace.
 * @param {ReturnType<import('../agentLoop.js').deriveToolEffects>} eff
 * @param {ToolEffectAcc} acc
 * @returns {{ stage: 'retrieve'|'tool', status: string } | null}
 */
export function applyToolEffects(eff, acc) {
  if (eff.gatheredContext) acc.gatheredContext = true;
  if (eff.ragChunk) acc.ragChunks.push(eff.ragChunk);
  /** @type {{ stage: 'retrieve'|'tool', status: string } | null} */
  let mark = null;
  if (eff.retrieveStage) mark = { stage: 'retrieve', status: String(eff.retrieveStage) };
  else if (eff.toolErrorStage) mark = { stage: 'tool', status: 'error' };
  if (eff.wroteFile) { acc.wroteFile = true; acc.writtenFiles.push(eff.wroteFile); }
  if (eff.knownPath) acc.knownExistingPaths.push(eff.knownPath);
  if (eff.committed) acc.committed = true;
  return mark;
}

export default applyToolEffects;
